import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Save, Printer, DoorClosed, Calculator } from "lucide-react";
import { openReport, buildReportName } from "../lib/reportViewerBus";
import { WinButton } from "./WinComponents";
import MoneyInput from "./MoneyInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODES = ["Hebdomadaire", "2Semaines", "BiMensuelle", "Mensuelle"];
const TYPES_VERS = ["Argent comptant", "Chèque", "Dépôts bancaires"];

const today = () => new Date().toISOString().slice(0, 10);
const fmt$ = (v) => `${(Number(v) || 0).toFixed(2)}$`;

/**
 * Modal Entente de paiement (Article 29) — parité frmEntente legacy.
 * Tables Themis : clentent + clversem. Ouvert depuis l'attestation.
 */
export default function EntenteModal({ noref, regbur, onClose, showPopup, onSaved, source = "attestation" }) {
  // Endpoint entente : attestation ou refus (clentent/clversem partagés,
  // contexte + indicateur différents côté serveur).
  const enteneBase = `${API}/${source === "refus" ? "refus" : "attestations"}/${encodeURIComponent(noref)}/entente`;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ctx, setCtx] = useState({});
  const [form, setForm] = useState({
    clentypeent: 1,
    clendate: today(),
    clenmont: 0,
    clennbvers: 1,
    clendateprvers: today(),
    clenperiod: "Mensuelle",
    clentypvers: "Chèque",
    clenlibelcheq: "",
    clenadrcheq: "",
    clennatserv: "",
    clenavosign: "",
    dernier_versement: 0,
    versement: 0,      // contribution max (lecture seule)
    coureelpre: 0,     // coût réel prévu (lecture seule)
  });
  const [versements, setVersements] = useState([]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(enteneBase);
        if (!alive) return;
        setCtx(data.contexte || {});
        if (data.exists && data.entente) {
          const e = data.entente;
          setForm((f) => ({
            ...f,
            clentypeent: Number(e.clentypeent ?? 1),
            clendate: (e.clendate || today()).slice(0, 10),
            clenmont: Number(e.clenmont || 0),
            clennbvers: Number(e.clennbvers || 1),
            clendateprvers: (e.clendateprvers || today()).slice(0, 10),
            clenperiod: e.clenperiod || "Mensuelle",
            clentypvers: e.clentypvers || "Chèque",
            clenlibelcheq: e.clenlibelcheq || "",
            clenadrcheq: e.clenadrcheq || "",
            clennatserv: e.clennatserv || "",
            clenavosign: e.clenavosign || "",
            dernier_versement: (data.versements?.length
              ? data.versements[data.versements.length - 1].montant : 0),
            versement: Number(e.versement || data.contexte?.contrib_max || 0),
            coureelpre: Number(e.coureelpre || data.contexte?.coureelpre || 0),
          }));
          setVersements(data.versements || []);
        } else {
          // Valeurs par défaut dérivées de l'attestation (parité NouvEntente).
          const c = data.contexte || {};
          const solde = Math.max((c.contrib_max || 0) - 50, 0);
          setForm((f) => ({
            ...f,
            clenmont: solde,
            versement: c.contrib_max || 0,
            coureelpre: c.coureelpre || 0,
            clennatserv: (c.nature || "").slice(0, 40),
            clenavosign: c.avocat_sign || "",
          }));
        }
      } catch (err) {
        showPopup?.(err?.response?.data?.detail || "Erreur au chargement de l'entente.",
          "error", "Entente");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [noref]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleCalculer = useCallback(async () => {
    if (!form.clenmont || form.clenmont <= 0)
      return showPopup?.("Saisissez le montant à payer (solde) avant de calculer.", "warning", "Entente");
    if (!form.clennbvers || form.clennbvers < 1)
      return showPopup?.("Le nombre de versements doit être ≥ 1.", "warning", "Entente");
    try {
      const { data } = await axios.post(`${API}/entente/compute-versements`, {
        montant: Number(form.clenmont),
        nb_versements: Number(form.clennbvers),
        dernier_versement: Number(form.dernier_versement) || 0,
        periodicite: form.clenperiod,
        date_premier_versement: form.clendateprvers,
      });
      setVersements(data.versements || []);
      if (data.versements?.length)
        setField("dernier_versement", data.versements[data.versements.length - 1].montant);
    } catch (err) {
      showPopup?.(err?.response?.data?.detail || "Erreur de calcul de l'échéancier.", "error", "Entente");
    }
  }, [form, showPopup]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (form.clentypeent === 1 && versements.length === 0)
      return showPopup?.("Cliquez « Calculer » pour générer l'échéancier avant d'enregistrer.", "warning", "Entente");
    setSaving(true);
    try {
      await axios.put(enteneBase, {
        clentypeent: form.clentypeent,
        clendate: form.clendate,
        clenmont: Number(form.clenmont),
        clennbvers: Number(form.clennbvers),
        clendateprvers: form.clendateprvers,
        clenperiod: form.clenperiod,
        clentypvers: form.clentypvers,
        clenlibelcheq: form.clenlibelcheq,
        clenadrcheq: form.clenadrcheq,
        clennatserv: form.clennatserv,
        clenavosign: form.clenavosign,
        versement: Number(form.versement),
        coureelpre: Number(form.coureelpre),
        versements: form.clentypeent === 1 ? versements : [],
      });
      showPopup?.("Entente de paiement enregistrée.", "info", "Entente");
      onSaved?.();
    } catch (err) {
      showPopup?.(err?.response?.data?.detail || "Erreur à l'enregistrement de l'entente.", "error", "Entente");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const url = `${API}/entente/pdf?noref=${encodeURIComponent(noref)}&regbur=${encodeURIComponent(regbur)}`;
    openReport({ url, title: "Entente de paiement",
      fileName: buildReportName("ENTENTE", noref) });
  };

  const lbl = { fontSize: 12, fontWeight: 600, color: "#243", marginBottom: 2, display: "block" };
  const cell = { display: "flex", flexDirection: "column", gap: 2 };

  return (
    <div className="popup-overlay" data-testid="modal-entente" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 760, maxWidth: "96vw", maxHeight: "94vh", background: "#eef2f6",
        border: "1px solid #9fb3c8", borderRadius: 4, display: "flex", flexDirection: "column",
      }}>
        {/* En-tête */}
        <div style={{
          background: "#1e3a5f", color: "#fff", padding: "8px 12px", display: "flex",
          justifyContent: "space-between", alignItems: "center", fontWeight: 600,
        }}>
          <span data-testid="entente-titre">
            Entente de paiement — {ctx.requerant_prenom} {ctx.requerant_nom}
            <span style={{ color: "#bcd", marginLeft: 8 }}>Dossier {ctx.nodossier || noref}</span>
          </span>
          <button onClick={onClose} data-testid="btn-entente-close-x"
            style={{ background: "transparent", color: "#fff", border: "none", fontSize: 18, cursor: "pointer" }}>×</button>
        </div>

        {/* Barre outils */}
        <div className="win-toolbar" style={{ padding: "8px 12px", gap: 8 }}>
          <WinButton IconComponent={Calculator} onClick={handleCalculer} title="Calculer l'échéancier des versements" data-testid="btn-entente-calculer">Calculer</WinButton>
          <WinButton IconComponent={Save} onClick={handleSave} disabled={saving || loading} title="Enregistrer l'entente" data-testid="btn-entente-save">Enregistrer</WinButton>
          <WinButton IconComponent={Printer} onClick={handlePrint} disabled={loading} title="Imprimer l'entente (langue du client)" data-testid="btn-entente-print">Imprimer</WinButton>
          <WinButton IconComponent={DoorClosed} onClick={onClose} title="Fermer" data-testid="btn-entente-close">Fermer</WinButton>
        </div>

        {/* Corps */}
        <div style={{ padding: 14, overflowY: "auto" }}>
          {loading ? (
            <div data-testid="entente-loading" style={{ padding: 30, textAlign: "center", color: "#567" }}>Chargement…</div>
          ) : (
            <>
              {/* Montants (lecture seule) */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div style={cell}>
                  <label style={lbl}>Contribution maximale</label>
                  <input className="win-input" readOnly value={fmt$(form.versement)} data-testid="entente-contribmax"
                    style={{ textAlign: "right", background: "#f3f3f3" }} />
                </div>
                <div style={cell}>
                  <label style={lbl}>Coût réel prévu</label>
                  <input className="win-input" readOnly value={fmt$(form.coureelpre)} data-testid="entente-coutprev"
                    style={{ textAlign: "right", background: "#f3f3f3" }} />
                </div>
              </div>

              {/* Type d'entente */}
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Type d'entente</label>
                <label style={{ marginRight: 18, fontSize: 13 }}>
                  <input type="radio" name="typeent" checked={form.clentypeent === 0}
                    onChange={() => setField("clentypeent", 0)} data-testid="entente-type-15j" /> Paiement dans les 15 jours
                </label>
                <label style={{ fontSize: 13 }}>
                  <input type="radio" name="typeent" checked={form.clentypeent === 1}
                    onChange={() => setField("clentypeent", 1)} data-testid="entente-type-versements" /> Versements multiples
                </label>
              </div>

              {/* Détails paiement */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div style={cell}>
                  <label style={lbl}>Date de l'entente</label>
                  <input type="date" className="win-input" value={form.clendate}
                    onChange={(e) => setField("clendate", e.target.value)} data-testid="entente-date" />
                </div>
                <div style={cell}>
                  <label style={lbl}>Montant à payer (solde)</label>
                  <MoneyInput value={form.clenmont} onChange={(v) => setField("clenmont", v)} testId="entente-montant" />
                </div>
                <div style={cell}>
                  <label style={lbl}>Avocat signataire</label>
                  <input className="win-input" value={form.clenavosign} maxLength={6}
                    onChange={(e) => setField("clenavosign", e.target.value)} data-testid="entente-avosign" />
                </div>
              </div>

              {form.clentypeent === 1 && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div style={cell}>
                    <label style={lbl}>Nombre de versements</label>
                    <input type="number" min={1} className="win-input" value={form.clennbvers}
                      onChange={(e) => setField("clennbvers", parseInt(e.target.value || "1", 10))}
                      data-testid="entente-nbvers" style={{ textAlign: "right" }} />
                  </div>
                  <div style={cell}>
                    <label style={lbl}>Périodicité</label>
                    <select className="win-input" value={form.clenperiod}
                      onChange={(e) => setField("clenperiod", e.target.value)} data-testid="entente-periode">
                      {PERIODES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div style={cell}>
                    <label style={lbl}>Date 1er versement</label>
                    <input type="date" className="win-input" value={form.clendateprvers}
                      onChange={(e) => setField("clendateprvers", e.target.value)} data-testid="entente-datepremvers" />
                  </div>
                  <div style={cell}>
                    <label style={lbl}>Type de versement</label>
                    <select className="win-input" value={form.clentypvers}
                      onChange={(e) => setField("clentypvers", e.target.value)} data-testid="entente-typevers">
                      {TYPES_VERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Nature du service + chèque */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginBottom: 12 }}>
                <div style={cell}>
                  <label style={lbl}>Nature du service</label>
                  <input className="win-input" value={form.clennatserv} maxLength={40}
                    onChange={(e) => setField("clennatserv", e.target.value)} data-testid="entente-natserv" />
                </div>
              </div>
              {form.clentypvers === "Chèque" && form.clentypeent === 1 && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div style={cell}>
                    <label style={lbl}>Libellé du chèque</label>
                    <input className="win-input" value={form.clenlibelcheq} maxLength={40}
                      onChange={(e) => setField("clenlibelcheq", e.target.value)} data-testid="entente-libelcheq" />
                  </div>
                  <div style={cell}>
                    <label style={lbl}>Adresse du chèque</label>
                    <input className="win-input" value={form.clenadrcheq} maxLength={40}
                      onChange={(e) => setField("clenadrcheq", e.target.value)} data-testid="entente-adrcheq" />
                  </div>
                </div>
              )}

              {/* Échéancier */}
              {form.clentypeent === 1 && (
                <div>
                  <label style={lbl}>Échéancier des versements</label>
                  <table className="win-table" data-testid="entente-versements-table"
                    style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#dbe6f0" }}>
                        <th style={{ border: "1px solid #b8c8d8", padding: "4px 8px", textAlign: "left" }}>#</th>
                        <th style={{ border: "1px solid #b8c8d8", padding: "4px 8px", textAlign: "left" }}>Date</th>
                        <th style={{ border: "1px solid #b8c8d8", padding: "4px 8px", textAlign: "right" }}>Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versements.length === 0 ? (
                        <tr><td colSpan={3} style={{ padding: 10, textAlign: "center", color: "#789" }}
                          data-testid="entente-versements-empty">Cliquez « Calculer » pour générer l'échéancier.</td></tr>
                      ) : versements.map((v, i) => (
                        <tr key={i} data-testid={`entente-versement-row-${i}`}>
                          <td style={{ border: "1px solid #d3dde7", padding: "3px 8px" }}>{i + 1}</td>
                          <td style={{ border: "1px solid #d3dde7", padding: "3px 8px" }}>{v.date}</td>
                          <td style={{ border: "1px solid #d3dde7", padding: "3px 8px", textAlign: "right" }}>{fmt$(v.montant)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {versements.length > 0 && (
                      <tfoot>
                        <tr style={{ fontWeight: 600, background: "#f0f4f8" }}>
                          <td colSpan={2} style={{ border: "1px solid #d3dde7", padding: "3px 8px", textAlign: "right" }}>Total</td>
                          <td style={{ border: "1px solid #d3dde7", padding: "3px 8px", textAlign: "right" }} data-testid="entente-versements-total">
                            {fmt$(versements.reduce((s, v) => s + (Number(v.montant) || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
